import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom' // Link used in IoC tab
import {
  ChevronLeft, Shield, AlertTriangle, Clock,
  ExternalLink, Bookmark, Newspaper, Info, RefreshCw,
  Flag, Paperclip, Users as UsersIcon, Bug, Lock, Wifi,
  AlertOctagon, Sparkles, Activity, Code, Copy, Star, GitBranch,
  TrendingUp, ShieldCheck, Zap,
} from 'lucide-react'
import { defangIoc, copyRefanged } from '../lib/defang'
import { clsx } from 'clsx'
import EpssBadge from '../components/EpssBadge'
import RelatedItems from '../components/RelatedItems'
import TTEvsMTTR from '../components/TTEvsMTTR'
import type { TTEData } from '../components/TTEBadge'
import { tteTone } from '../components/TTEBadge'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface CvssMetric { code: string; label: string; help?: string }

interface StoryEvent {
  ts: string
  kind: 'disclosed' | 'news' | 'kev' | 'exploit' | 'ioc' | 'ransomware'
  label: string
  detail: string
  days_after_disclosure?: number | null
}

interface CveDetail {
  cve_id: string
  description: string
  storyline?: StoryEvent[]
  cvss: {
    score: number | null
    severity: string
    vector: string
    version: string
    metrics: Record<string, CvssMetric>
  }
  epss: { score: number; percentile: number; date: string } | null
  tte: TTEData | null
  risk_score: number
  kev: boolean
  ransomware_use: string | null
  cwes: { id: string; description: string }[]
  references: { url: string; source: string; tags: string[] }[]
  affected_products: string[]
  published: string | null
  modified: string | null
  article_mentions: {
    title: string
    summary: string
    source: string
    url: string
    published_at: string
    translated: boolean
    language?: string
  }[]
  article_count: number
  mention_timeline: { date: string; count: number }[]
  iocs: {
    ioc: string
    ioc_type: string
    malware: string
    threat_type: string
    confidence: number
    first_seen: string
    reference: string
    tags: string[]
  }[]
  ioc_count: number
  exploits: {
    kind: string   // 'github-repo' | 'exploit-db'
    name: string
    url: string
    stars?: number
    description?: string
    updated?: string
    language?: string
    id?: number
  }[]
  exploit_count: number
  ai_narrative: string | null
  source_nvd_url: string
}

type Tab = 'overview' | 'news' | 'lifecycle' | 'ioc' | 'exploits'

// ── Circular gauge ───────────────────────────────────────────────────────────

function Gauge({
  label, value, max = 100, unit = '', color, onInfo,
}: {
  label: string
  value: number | null
  max?: number
  unit?: string
  color: 'rose' | 'amber' | 'emerald' | 'blue' | 'text-muted'
  onInfo?: string
}) {
  const pct = value != null && max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  const palette = {
    rose:         { stroke: '#f43f5e', glow: 'rgba(244,63,94,0.35)',  text: '#fda4af' },
    amber:        { stroke: '#f59e0b', glow: 'rgba(245,158,11,0.35)', text: '#fcd34d' },
    emerald:      { stroke: '#10b981', glow: 'rgba(16,185,129,0.30)', text: '#6ee7b7' },
    blue:         { stroke: '#3b82f6', glow: 'rgba(59,130,246,0.30)', text: '#93c5fd' },
    'text-muted': { stroke: '#52525b', glow: 'rgba(82,82,91,0.20)',   text: '#a1a1aa' },
  }[color]

  const size = 120
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const dash = circumference * (pct / 100)
  const cx = size / 2
  const gid = `grad-${color}-${label}`

  const displayValue = value != null
    ? (unit === '%' ? value.toFixed(0) : value.toFixed(1))
    : '—'

  return (
    <div className="flex flex-col items-center" title={onInfo}>
      <div className="text-[10px] text-text-muted uppercase tracking-wider font-semibold flex items-center gap-1 mb-2">
        {label}
        {onInfo && <Info size={9} className="opacity-60" />}
      </div>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <defs>
            <linearGradient id={gid} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor={palette.stroke} stopOpacity="1" />
              <stop offset="100%" stopColor={palette.stroke} stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <circle cx={cx} cy={cx} r={radius} fill="none" stroke="#1f1f24" strokeWidth={stroke} />
          {value != null && (
            <circle
              cx={cx} cy={cx} r={radius}
              fill="none" stroke={`url(#${gid})`} strokeWidth={stroke} strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference}`}
              style={{
                transition: 'stroke-dasharray 700ms ease-out',
                filter: `drop-shadow(0 0 6px ${palette.glow})`,
              }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold font-mono leading-none" style={{ color: palette.text }}>
            {displayValue}
          </div>
          <div className="text-[10px] text-text-muted mt-1">
            {unit === '%' ? `${max}${unit} scale` : `/ ${max}`}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── CVSS metric tile ─────────────────────────────────────────────────────────

const METRIC_LABELS: Record<string, { label: string; icon: React.ElementType }> = {
  AV: { label: 'Attack Vector (AV)',      icon: Paperclip },
  AC: { label: 'Attack Complexity (AC)',  icon: Sparkles },
  PR: { label: 'Privileges Required (PR)', icon: Lock },
  UI: { label: 'User Interaction (UI)',   icon: UsersIcon },
  S:  { label: 'Scope (S)',                icon: Flag },
  C:  { label: 'Confidentiality (C)',     icon: Lock },
  I:  { label: 'Integrity (I)',            icon: Shield },
  A:  { label: 'Availability (A)',        icon: Wifi },
}

function MetricTile({ mkey, metric }: { mkey: string; metric: CvssMetric }) {
  const meta = METRIC_LABELS[mkey] || { label: mkey, icon: Info }
  const Icon = meta.icon
  const label = metric.label?.toUpperCase() || metric.code
  const labelColor =
    ['HIGH', 'NETWORK', 'NONE'].includes(label) && mkey === 'AV' ? 'text-rose' :
    ['HIGH'].includes(label) && (mkey === 'C' || mkey === 'I' || mkey === 'A') ? 'text-rose' :
    ['CHANGED'].includes(label) ? 'text-amber-light' :
    ['LOW', 'ADJACENT', 'REQUIRED', 'UNCHANGED', 'PHYSICAL'].includes(label) ? 'text-amber-light' :
    'text-emerald'
  return (
    <div className="card p-3 flex flex-col items-center text-center gap-1">
      <Icon size={14} className="text-text-muted" />
      <div className="text-[9px] text-text-muted uppercase tracking-wider">{meta.label}</div>
      <div className={clsx('text-sm font-bold', labelColor)}>{label}</div>
    </div>
  )
}

// ── Vulnerability Life Cycle chart (daily mentions) ─────────────────────────

function LifeCycleChart({ points }: { points: { date: string; count: number }[] }) {
  if (points.length === 0) return null
  // Fill in missing days with zero so the timeline is continuous
  const firstDate = new Date(points[0].date)
  const lastDate = new Date(points[points.length - 1].date)
  const dayMs = 86400_000
  const countByDate: Record<string, number> = Object.fromEntries(points.map(p => [p.date, p.count]))
  const filled: { date: string; count: number }[] = []
  for (let d = new Date(firstDate); d <= lastDate; d = new Date(d.getTime() + dayMs)) {
    const iso = d.toISOString().slice(0, 10)
    filled.push({ date: iso, count: countByDate[iso] || 0 })
  }

  const maxV = Math.max(1, ...filled.map(p => p.count))
  const width = 720
  const height = 140
  const stepX = filled.length > 1 ? width / (filled.length - 1) : width
  const path = filled
    .map((p, i) => {
      const x = i * stepX
      const y = height - (p.count / maxV) * (height - 12) - 2
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const peak = filled.reduce((m, p) => (p.count > m.count ? p : m), filled[0])
  const total = filled.reduce((s, p) => s + p.count, 0)

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lifecycle-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L${(filled.length - 1) * stepX},${height} L0,${height} Z`}
              fill="url(#lifecycle-fill)" />
        <path d={path} fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Peak marker */}
        {peak.count > 0 && (() => {
          const idx = filled.indexOf(peak)
          const px = idx * stepX
          const py = height - (peak.count / maxV) * (height - 12) - 2
          return <circle cx={px} cy={py} r="3" fill="#f43f5e" />
        })()}
      </svg>
      <div className="flex items-center justify-between mt-1 text-[9px] text-text-muted">
        <span>{filled[0].date}</span>
        {peak.count > 0 && (
          <span className="text-rose-light">Peak {peak.count} on {peak.date}</span>
        )}
        <span>{filled[filled.length - 1].date}</span>
      </div>
      <div className="text-[10px] text-text-secondary mt-1">
        Total: {total} mentions over {filled.length} days
      </div>
    </div>
  )
}

// ── EPSS trajectory sparkline ────────────────────────────────────────────────

function EpssTrajectoryCard({ cveId }: { cveId: string }) {
  const [points, setPoints] = useState<{ date: string; score: number; percentile: number }[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    import('../lib/api').then(({ api }) => {
      api.get(`/api/cyber/cve/${encodeURIComponent(cveId)}/epss-history`, { params: { days: 180 } })
        .then(r => { if (mounted) setPoints(r.data.points || []) })
        .catch(() => { if (mounted) setPoints([]) })
        .finally(() => { if (mounted) setLoading(false) })
    })
    return () => { mounted = false }
  }, [cveId])

  if (loading) return null

  const hasData = points.length >= 2

  const width = 720
  const height = 120
  const stepX = hasData ? width / (points.length - 1) : width
  const path = points.map((p, i) => {
    const x = i * stepX
    const y = height - p.percentile * (height - 8) - 4   // percentile 0..1 → height
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  const first = points[0]
  const last = points[points.length - 1]
  const delta = hasData ? (last.percentile - first.percentile) : 0
  const trendColor = delta > 0.05 ? '#f43f5e' : delta < -0.05 ? '#10b981' : '#f59e0b'

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <TrendingUp size={14} style={{ color: trendColor }} /> EPSS trajectory
        </h3>
        <span className="text-[10px] text-text-muted">
          {hasData
            ? <>
                {Math.round(first.percentile * 100)}% → <b style={{ color: trendColor }}>{Math.round(last.percentile * 100)}%</b>
                &nbsp;· {delta > 0 ? '+' : ''}{(delta * 100).toFixed(1)}pp over {points.length} days
              </>
            : 'No history yet — first snapshot after next daily job'}
        </span>
      </div>
      {!hasData ? (
        <p className="text-[11px] text-text-muted py-6 text-center">
          EPSS trajectory will chart here once we capture daily snapshots (≥2 data points).
          Trigger <code className="text-text-secondary">POST /api/cyber/epss/snapshot</code> to seed.
        </p>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-28" preserveAspectRatio="none">
          <defs>
            <linearGradient id="epss-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={trendColor} stopOpacity="0.4" />
              <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${path} L${(points.length - 1) * stepX},${height} L0,${height} Z`} fill="url(#epss-fill)" />
          <path d={path} fill="none" stroke={trendColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <p className="text-[9px] text-text-muted mt-2 italic">
        EPSS percentile = FIRST.org's probability-of-exploitation ranking. A rising line is often the
        earliest signal that a CVE is being weaponised — frequently precedes KEV listing by days.
      </p>
    </div>
  )
}

function severityColor(sev: string | null | undefined): 'rose' | 'amber' | 'emerald' | 'text-muted' {
  const s = (sev || '').toLowerCase()
  if (s === 'critical') return 'rose'
  if (s === 'high') return 'rose'
  if (s === 'medium') return 'amber'
  if (s === 'low') return 'emerald'
  return 'text-muted'
}

function riskColor(score: number): 'rose' | 'amber' | 'emerald' | 'blue' {
  if (score >= 70) return 'rose'
  if (score >= 50) return 'amber'
  if (score >= 30) return 'blue'
  return 'emerald'
}

function relativeTime(ts: string | null | undefined): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) } catch { return ts }
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function CveDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<CveDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('overview')
  const [bookmarked, setBookmarked] = useState(false)

  const fetchDetail = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data } = await api.get(`/api/cyber/cve/${encodeURIComponent(id)}`)
      setData(data)
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { fetchDetail() }, [fetchDetail])

  const bookmark = async () => {
    if (!data) return
    try {
      await api.post('/api/content/bookmarks', {
        article_title: `${data.cve_id}: ${data.description.slice(0, 100)}`,
        article_url: data.source_nvd_url,
        article_source: 'NVD',
      })
      setBookmarked(true)
    } catch { /* ignore */ }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
        <span className="ml-3 text-[11px] text-text-muted">Fetching CVE + EPSS + NVD…</span>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="card p-8 text-center">
        <AlertTriangle size={28} className="text-rose mx-auto mb-2" />
        <div className="text-sm text-text-primary">CVE {id} not found</div>
        <button type="button" onClick={() => navigate('/app/cyber')} className="btn-primary text-xs mt-3">
          Back to CVE feed
        </button>
      </div>
    )
  }

  const sev = severityColor(data.cvss.severity)
  const sevClass = sev === 'rose' ? 'bg-rose/10 text-rose border-rose/30'
    : sev === 'amber' ? 'bg-amber/10 text-amber-light border-amber/30'
    : sev === 'emerald' ? 'bg-emerald/10 text-emerald border-emerald/30'
    : 'bg-surface-raised text-text-muted border-border'

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-ghost p-1.5"
            title="Back"
            aria-label="Back"
          >
            <ChevronLeft size={16} />
          </button>
          <h1 className="text-xl font-bold text-text-primary font-mono">{data.cve_id}</h1>
          <span className={clsx('px-2 py-0.5 rounded border text-[10px] font-semibold uppercase', sevClass)}>
            {data.cvss.severity}
          </span>
          {data.kev && (
            <span className="px-2 py-0.5 rounded border border-rose/40 bg-rose/10 text-rose text-[10px] font-semibold uppercase flex items-center gap-1 animate-pulse">
              <AlertOctagon size={10} /> In the wild (KEV)
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={bookmark}
          className={clsx(
            'btn-secondary text-xs flex items-center gap-1.5',
            bookmarked && 'bg-amber/10 border-amber/30 text-amber-light',
          )}
        >
          <Bookmark size={12} fill={bookmarked ? 'currentColor' : 'none'} />
          {bookmarked ? 'Bookmarked' : 'Subscribe'}
        </button>
      </div>

      {/* Exploitation timing — the headline shift: will this be exploited
          before we can patch it? Shown up top alongside the severity badge. */}
      {data.tte && <ExploitationTimingPanel tte={data.tte} />}

      {/* Top row: left column (tabs + body) + right column (gauges + sidebar) */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)] gap-4">
        {/* LEFT */}
        <div className="space-y-4 min-w-0">
          {/* Tabs */}
          <div className="flex items-center gap-0 border-b border-border">
            {([
              { id: 'overview',  label: 'Overview',             icon: Info },
              { id: 'news',      label: 'News',                 icon: Newspaper, count: data.article_count },
              { id: 'lifecycle', label: 'Vulnerability Life Cycle', icon: Activity },
              { id: 'ioc',       label: 'IoC',                  icon: Bug,   count: data.ioc_count },
              { id: 'exploits',  label: 'Exploits / PoCs',      icon: Code,  count: data.exploit_count },
            ] as const).map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  'px-4 py-2 text-[11px] font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors',
                  tab === t.id ? 'border-blue text-blue-light' : 'border-transparent text-text-muted hover:text-text-primary',
                )}
              >
                <t.icon size={12} /> {t.label}
                {'count' in t && t.count != null && t.count > 0 && (
                  <span className={clsx(
                    'ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono',
                    tab === t.id ? 'bg-blue/20 text-blue-light' : 'bg-surface-raised text-text-secondary',
                  )}>
                    {t.count > 10 ? '10+' : t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'overview' && data.storyline && data.storyline.length > 1 && (
            <Storyline events={data.storyline} />
          )}
          {tab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4">
              {/* AI narrative or NVD description */}
              <article className="space-y-3">
                {data.ai_narrative ? (
                  <div className="space-y-3">
                    {data.ai_narrative.split(/\n\n+/).map((para, i) => {
                      // Use the first paragraph as a headline
                      if (i === 0) {
                        const first = para.split(/\.\s+/)[0]
                        const rest = para.slice(first.length + 1).trim()
                        return (
                          <div key={i}>
                            <h2 className="text-[14px] font-bold text-text-primary mb-1.5">{first}.</h2>
                            {rest && <p className="text-[11px] text-text-secondary leading-relaxed">{rest}</p>}
                          </div>
                        )
                      }
                      // Other paragraphs — bold the leading label if there is one ("The Impact:", "Severity & Action:", etc.)
                      const match = para.match(/^([A-Z][^:.]{3,40}:)\s*/)
                      return (
                        <div key={i}>
                          {match ? (
                            <>
                              <h3 className="text-[12px] font-bold text-text-primary mt-3 mb-1">{match[1]}</h3>
                              <p className="text-[11px] text-text-secondary leading-relaxed">{para.slice(match[0].length)}</p>
                            </>
                          ) : (
                            <p className="text-[11px] text-text-secondary leading-relaxed">{para}</p>
                          )}
                        </div>
                      )
                    })}
                    <p className="text-[9px] text-text-muted italic pt-2 border-t border-border/50">
                      AI-generated briefing. Authoritative source: <a href={data.source_nvd_url} target="_blank" rel="noreferrer noopener" className="text-blue-light hover:underline">NVD</a>.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-[13px] font-bold text-text-primary mb-2">{data.cve_id} description</h2>
                    <p className="text-[11px] text-text-secondary leading-relaxed whitespace-pre-line">{data.description || 'No description available yet.'}</p>
                  </div>
                )}
              </article>

              {/* CVSS v3.1 Analysis panel */}
              <div className="space-y-2">
                <h2 className="text-[13px] font-semibold text-text-primary">CVSS {data.cvss.version} Analysis</h2>
                <div className="grid grid-cols-2 gap-2">
                  <div className="card p-3 flex flex-col items-center text-center gap-1">
                    <Flag size={14} className="text-text-muted" />
                    <div className="text-[9px] text-text-muted uppercase tracking-wider">Base Score</div>
                    <div className={clsx('text-lg font-bold font-mono',
                      sev === 'rose' ? 'text-rose' : sev === 'amber' ? 'text-amber-light' : 'text-emerald'
                    )}>
                      {data.cvss.score?.toFixed(1) ?? '—'}
                    </div>
                  </div>
                  {Object.entries(data.cvss.metrics).slice(0, 5).map(([k, m]) => (
                    <MetricTile key={k} mkey={k} metric={m} />
                  ))}
                </div>
                <LinkedActors cveId={data.cve_id} />
                <RelatedItems entityType="cve" entityKey={data.cve_id} className="mt-2" />
              </div>
            </div>
          )}

          {tab === 'news' && (
            <div className="space-y-2">
              {data.article_mentions.length === 0 ? (
                <div className="card p-6 text-center text-[11px] text-text-muted">
                  No news articles mention {data.cve_id} in our ingested feeds yet.
                </div>
              ) : (
                data.article_mentions.map((a, i) => (
                  <a
                    key={i}
                    href={a.url || '#'}
                    target={a.url ? '_blank' : undefined}
                    rel="noreferrer noopener"
                    className="card p-3 block hover:bg-surface-raised/30 transition-colors"
                  >
                    <div className="flex items-center gap-1.5 mb-1 text-[10px] text-text-muted">
                      <span className="text-blue-light font-semibold">{a.source}</span>
                      {a.translated && a.language && (
                        <span className="px-1 py-0.5 rounded bg-blue/10 text-blue-light text-[9px] font-mono">
                          {a.language.toUpperCase()} → EN
                        </span>
                      )}
                      <span className="ml-auto">
                        <Clock size={10} className="inline mr-0.5" />
                        {relativeTime(a.published_at)}
                      </span>
                    </div>
                    <div className="text-[12px] font-semibold text-text-primary leading-snug">{a.title}</div>
                    {a.summary && (
                      <p className="text-[10px] text-text-secondary mt-1 leading-relaxed line-clamp-2">{a.summary}</p>
                    )}
                  </a>
                ))
              )}
            </div>
          )}

          {tab === 'lifecycle' && (
            <div className="space-y-3">
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                    <TrendingUp size={14} className="text-blue" /> Public-attention timeline
                  </h3>
                  <span className="text-[10px] text-text-muted">
                    {data.mention_timeline.reduce((s, p) => s + p.count, 0)} mentions · last 90 days · 460+ sources aggregated
                  </span>
                </div>
                {data.mention_timeline.length === 0 ? (
                  <p className="text-[11px] text-text-muted py-6 text-center">
                    No mentions of {data.cve_id} in our ingested feeds over the last 90 days.
                  </p>
                ) : (
                  <LifeCycleChart points={data.mention_timeline} />
                )}
                <p className="text-[9px] text-text-muted mt-2 italic">
                  Social / Twitter posts are proxied by article mentions across our 460+ RSS sources
                  (vendor blogs, national CERTs, security vendors, mainstream tech press, social
                  community feeds). A direct X/Twitter scraper would extend this further but adds
                  auth complexity — we get 80% of the signal from the aggregated sources here.
                </p>
              </div>
              <EpssTrajectoryCard cveId={data.cve_id} />
            </div>
          )}

          {tab === 'ioc' && (
            <div className="space-y-3">
              {data.iocs.length === 0 ? (
                <div className="card p-6 text-center text-[11px] text-text-muted">
                  No IoCs tagged with {data.cve_id} on ThreatFox yet.
                  {' '}
                  <Link to="/app/cyber-warroom" className="text-blue-light hover:underline">Open Situation Room</Link>
                  {' '}for broader IoC context.
                </div>
              ) : (
                <div className="card overflow-hidden">
                  <div className="p-3 border-b border-border flex items-center justify-between bg-rose/5">
                    <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                      <Bug size={14} className="text-rose" /> IoCs tagged with {data.cve_id}
                    </h3>
                    <span className="text-[10px] text-text-muted">
                      {data.iocs.length} indicators · defanged · source: ThreatFox (abuse.ch)
                    </span>
                  </div>
                  <table className="w-full text-[10px]">
                    <thead className="bg-surface-raised/40">
                      <tr className="border-b border-border text-text-muted">
                        <th className="py-1.5 px-2 text-left font-semibold">Indicator</th>
                        <th className="py-1.5 px-2 text-left font-semibold">Type</th>
                        <th className="py-1.5 px-2 text-left font-semibold">Malware</th>
                        <th className="py-1.5 px-2 text-right font-semibold">Conf</th>
                        <th className="py-1.5 px-2 text-left font-semibold">First seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.iocs.map((i, idx) => {
                        const defanged = defangIoc(i.ioc, i.ioc_type)
                        return (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="py-1.5 px-2 font-mono text-rose-light max-w-[220px] truncate" title={`DEFANGED — click icon to copy live: ${defanged}`}>
                              <span className="inline-flex items-center gap-1">
                                <button type="button" onClick={() => copyRefanged(defanged)} className="text-text-muted hover:text-rose shrink-0" title="Copy live indicator" aria-label="Copy live indicator">
                                  <Copy size={9} />
                                </button>
                                {defanged}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 text-text-secondary">{i.ioc_type}</td>
                            <td className="py-1.5 px-2 text-amber-light">{i.malware}</td>
                            <td className="py-1.5 px-2 text-right">
                              <span className={clsx('font-mono font-bold',
                                i.confidence >= 75 ? 'text-rose' : i.confidence >= 50 ? 'text-amber' : 'text-text-muted'
                              )}>{i.confidence}%</span>
                            </td>
                            <td className="py-1.5 px-2 text-text-muted font-mono">{i.first_seen?.slice(0, 16).replace('T', ' ')}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'exploits' && (
            <div className="space-y-3">
              {data.exploits.length === 0 ? (
                <div className="card p-6 text-center text-[11px] text-text-muted">
                  No public PoCs found on GitHub or ExploitDB for {data.cve_id}.
                  <div className="mt-2">
                    <a href={`https://github.com/search?q=${encodeURIComponent(data.cve_id)}&type=repositories`}
                       target="_blank" rel="noreferrer noopener"
                       className="text-blue-light hover:underline text-[10px]">Search GitHub manually</a>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.exploits.map((e, i) => (
                    <a key={i} href={e.url} target="_blank" rel="noreferrer noopener"
                       className="card p-3 block hover:bg-surface-raised/30 transition-colors">
                      <div className="flex items-center gap-2 mb-1 text-[10px]">
                        {e.kind === 'github-repo' ? (
                          <>
                            <GitBranch size={11} className="text-blue-light" />
                            <span className="text-blue-light font-semibold">GitHub</span>
                            {e.language && <span className="px-1 py-0.5 rounded bg-surface-raised text-text-secondary">{e.language}</span>}
                            {typeof e.stars === 'number' && (
                              <span className="flex items-center gap-0.5 text-amber-light ml-auto">
                                <Star size={9} fill="currentColor" /> {e.stars}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <Code size={11} className="text-rose" />
                            <span className="text-rose font-semibold">ExploitDB</span>
                          </>
                        )}
                        <ExternalLink size={9} className="text-text-muted ml-auto" />
                      </div>
                      <div className="text-[11px] font-semibold text-text-primary leading-snug">{e.name}</div>
                      {e.description && (
                        <p className="text-[10px] text-text-secondary mt-1 line-clamp-2">{e.description}</p>
                      )}
                      {e.updated && (
                        <div className="text-[9px] text-text-muted mt-1">updated {e.updated.slice(0, 10)}</div>
                      )}
                    </a>
                  ))}
                </div>
              )}
              <div className="text-[9px] text-text-muted italic">
                PoCs aggregated from GitHub repo search (with our GITHUB_TOKEN for higher rate limits) and ExploitDB's CVE search page. Sorted by stars for GitHub relevance.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-3 min-w-0">
          {/* Three gauges */}
          <div className="card p-5">
            <div className="grid grid-cols-3 gap-4 place-items-center">
              <Gauge label="CVSSv3" value={data.cvss.score} max={10}
                     color={severityColor(data.cvss.severity)}
                     onInfo="Common Vulnerability Scoring System base score" />
              <Gauge label="EPSS"
                     value={data.epss ? data.epss.score * 100 : null}
                     max={100} unit="%"
                     color={
                       data.epss && data.epss.score >= 0.90 ? 'rose' :
                       data.epss && data.epss.score >= 0.50 ? 'amber' :
                       data.epss && data.epss.score >= 0.10 ? 'blue' :
                       data.epss ? 'emerald' : 'text-muted'
                     }
                     onInfo="EPSS score — FIRST.org 30-day exploitation probability (0-100%)" />
              <Gauge label="Risk" value={data.risk_score} max={100}
                     color={riskColor(data.risk_score)}
                     onInfo="Composite: CVSS + EPSS + KEV + affected product breadth" />
            </div>
            {/* SANS-canonical EPSS band badge — sits below the gauges so the
                triage decision is obvious at a glance ("HIGH — patch this week") */}
            {data.epss && (
              <div className="mt-3 flex items-center justify-center">
                <EpssBadge score={data.epss.score} band={(data as any).epss_band} />
              </div>
            )}

            {/* Effective risk after compensating controls */}
            {(data as any).effective_risk != null && (data as any).credit_total > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-emerald" /> Effective Risk (after controls)
                  </h3>
                  <div className="text-right">
                    <div className="text-lg font-bold text-emerald-light">{(data as any).effective_risk}<span className="text-[10px] text-text-muted font-normal">/100</span></div>
                    <div className="text-[9px] text-emerald">−{(data as any).credit_total} credit</div>
                  </div>
                </div>
                <div className="space-y-1">
                  {((data as any).controls || []).slice(0, 8).map((c: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-[10px] border border-border/50 rounded px-2 py-1">
                      <span className="text-text-secondary">{c.control}</span>
                      <span className="text-emerald font-mono">−{c.credit}</span>
                    </div>
                  ))}
                  {((data as any).controls || []).length === 0 && (
                    <p className="text-[10px] text-text-muted italic">No controls matched this CVE's affected products.</p>
                  )}
                </div>
                <p className="text-[9px] text-text-muted mt-2 italic">
                  Credits from the defaults catalogue (CrowdStrike / Defender / SCCM / BMC Helix).
                  Override per-CVE via <code className="text-text-secondary">PUT /api/cyber/controls/{data.cve_id}</code>.
                </p>
              </div>
            )}
          </div>

          <div className="card p-3 space-y-2 text-[11px]">
            <Field label="Published Date" value={data.published?.slice(0, 10) || '—'} />
            <Field label="Modified Date" value={data.modified?.slice(0, 10) || '—'} />
            <Field label="Vector String" value={data.cvss.vector || '—'} mono />
            {data.kev && <Field label="Tags" value={<span className="px-1.5 py-0.5 rounded border border-rose/30 bg-rose/10 text-rose text-[10px] font-semibold">In The Wild</span>} />}
            {data.ransomware_use && data.ransomware_use !== 'Unknown' && (
              <Field label="Ransomware Use" value={data.ransomware_use} />
            )}

            {data.affected_products.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">Affected Products ({data.affected_products.length})</div>
                <div className="flex flex-wrap gap-1">
                  {data.affected_products.slice(0, 10).map((p, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary">{p}</span>
                  ))}
                  {data.affected_products.length > 10 && (
                    <span className="text-[9px] text-text-muted">+{data.affected_products.length - 10}</span>
                  )}
                </div>
              </div>
            )}

            {data.cwes.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">CWE</div>
                <div className="space-y-1">
                  {data.cwes.map(c => (
                    <a key={c.id} href={`https://cwe.mitre.org/data/definitions/${c.id.replace('CWE-', '')}.html`}
                       target="_blank" rel="noreferrer noopener"
                       className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1 block">
                      <Bug size={11} /> {c.id}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {data.references.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">References ({data.references.length})</div>
                <div className="space-y-0.5 max-h-40 overflow-y-auto">
                  {data.references.map((r, i) => {
                    const host = (() => { try { return new URL(r.url).hostname } catch { return r.url } })()
                    return (
                      <a key={i} href={r.url} target="_blank" rel="noreferrer noopener"
                         className="block text-[10px] text-blue-light hover:underline truncate" title={r.url}>
                        <ExternalLink size={9} className="inline mr-0.5" /> {host}
                      </a>
                    )
                  })}
                </div>
              </div>
            )}

            <a href={data.source_nvd_url} target="_blank" rel="noreferrer noopener"
               className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1 pt-1 border-t border-border/50">
              Open in NVD <ExternalLink size={10} />
            </a>
          </div>
        </aside>
      </div>
    </div>
  )
}

function ExploitationTimingPanel({ tte }: { tte: TTEData }) {
  const tone = tteTone(tte.predicted_tte_days)
  const accent = tte.losing ? 'border-rose/30 bg-rose/5' : 'border-emerald/30 bg-emerald/5'
  const ttdLabel = tte.predicted_tte_days <= 0 ? 'now'
    : tte.predicted_tte_days < 1 ? `${(tte.predicted_tte_days * 24).toFixed(0)}h`
    : `${tte.predicted_tte_days}d`
  const valTone = tone === 'red' ? 'text-rose-light' : tone === 'amber' ? 'text-amber-light' : 'text-blue-light'
  return (
    <div className={clsx('card p-4 border', accent)}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-bold text-text-primary flex items-center gap-2">
          <Clock size={14} className={tte.losing ? 'text-rose-light' : 'text-emerald'} /> Exploitation timing
          <span className="text-[10px] font-normal text-text-muted">predicted weaponization vs our patch window</span>
        </h2>
        <Link to="/app/exploitation-race" className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1">
          Exploitation Race <ExternalLink size={9} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4 items-center">
        <div className="space-y-2">
          <TTEvsMTTR tte={tte} />
          {tte.verdict && (
            <p className={clsx('text-[12px] font-medium leading-snug', tte.losing ? 'text-rose-light' : 'text-emerald')}>
              {tte.losing ? '⚠ ' : '✓ '}{tte.verdict}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded border border-border bg-surface-raised p-2">
              <div className="text-[9px] uppercase tracking-widest text-text-muted">Predicted TTE</div>
              <div className={clsx('text-lg font-bold font-mono mt-0.5', valTone)}>{ttdLabel}</div>
            </div>
            <div className="rounded border border-border bg-surface-raised p-2">
              <div className="text-[9px] uppercase tracking-widest text-text-muted">Our MTTR</div>
              <div className="text-lg font-bold font-mono mt-0.5 text-cyan">{tte.our_mttr_days ?? '—'}d</div>
            </div>
            <div className="rounded border border-border bg-surface-raised p-2">
              <div className="text-[9px] uppercase tracking-widest text-text-muted">Exposure</div>
              <div className={clsx('text-lg font-bold font-mono mt-0.5', tte.losing ? 'text-rose-light' : 'text-emerald')}>
                {tte.exposure_days != null ? `${tte.exposure_days > 0 ? '+' : ''}${tte.exposure_days}d` : '—'}
              </div>
            </div>
          </div>
          {tte.factors && tte.factors.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Why the window is short</div>
              <div className="flex flex-wrap gap-1">
                {tte.factors.map((f, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-surface-raised text-text-secondary inline-flex items-center gap-1">
                    {tte.ai_accelerated && f.toLowerCase().includes('ai') && <Zap size={9} className="text-amber-light" />}
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-[9px] text-text-muted mt-2 italic">
        Predicted TTE from the <Link to="/app/zero-day-clock" className="text-blue-light hover:underline">Zero Day Clock</Link> decay
        model, accelerated by KEV / EPSS / ransomware / AI-discovery. MTTR is the realistic per-asset-class patch window.
      </p>
    </div>
  )
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[9px] uppercase tracking-wider text-text-muted font-semibold shrink-0">{label}</span>
      <span className={clsx('text-[10px] text-text-secondary text-right min-w-0 truncate', mono && 'font-mono break-all')} title={typeof value === 'string' ? value : undefined}>
        {value}
      </span>
    </div>
  )
}

function Storyline({ events }: { events: StoryEvent[] }) {
  const kindStyle: Record<StoryEvent['kind'], { color: string; icon: React.ElementType; bg: string }> = {
    disclosed:  { color: 'text-blue-light',    icon: Info,           bg: 'bg-blue/15' },
    news:       { color: 'text-cyan',          icon: Newspaper,      bg: 'bg-cyan/15' },
    kev:        { color: 'text-rose',          icon: ShieldCheck,    bg: 'bg-rose/15' },
    exploit:    { color: 'text-amber-light',   icon: Code,           bg: 'bg-amber/15' },
    ioc:        { color: 'text-purple-300',    icon: Activity,       bg: 'bg-purple-500/15' },
    ransomware: { color: 'text-rose-light',    icon: AlertOctagon,   bg: 'bg-rose/20' },
  }
  // Velocity tag
  const kev = events.find(e => e.kind === 'kev')
  const exploit = events.find(e => e.kind === 'exploit')
  const fastWeapon = (kev && (kev.days_after_disclosure ?? 99) <= 7)
                  || (exploit && (exploit.days_after_disclosure ?? 99) <= 7)

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[12px] font-semibold text-text-primary flex items-center gap-2">
          <Activity size={12} className="text-blue-light" /> Storyline
          <span className="text-[9px] text-text-muted font-normal">disclosure → weaponization</span>
        </h2>
        {fastWeapon && (
          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose/20 text-rose-light border border-rose/40">
            ⚠ Fast weaponization
          </span>
        )}
      </div>
      <ol className="relative border-l border-border ml-2 space-y-2.5">
        {events.map((e, i) => {
          const s = kindStyle[e.kind] || kindStyle.disclosed
          const Icon = s.icon
          return (
            <li key={i} className="ml-4 relative">
              <span className={clsx('absolute -left-[22px] top-0.5 w-3 h-3 rounded-full flex items-center justify-center', s.bg)}>
                <Icon size={8} className={s.color} />
              </span>
              <div className="flex items-baseline justify-between gap-2">
                <span className={clsx('text-[10px] font-semibold', s.color)}>{e.label}</span>
                <span className="text-[9px] font-mono text-text-muted shrink-0">
                  {new Date(e.ts).toLocaleDateString()}
                  {typeof e.days_after_disclosure === 'number' && e.days_after_disclosure > 0 && (
                    <span className="ml-1.5 text-text-muted">+{e.days_after_disclosure}d</span>
                  )}
                </span>
              </div>
              {e.detail && <p className="text-[10px] text-text-secondary mt-0.5 line-clamp-2">{e.detail}</p>}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

interface LinkedActor {
  id: string
  name: string
  sponsor: string
  category: string
  matched_via: string
  structured: boolean
  evidence_url: string | null
  evidence_src: string | null
  aliases: string[]
}

function LinkedActors({ cveId }: { cveId: string }) {
  const [actors, setActors] = useState<LinkedActor[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    api.get(`/api/cyber/cve/${encodeURIComponent(cveId)}/actors`)
      .then(r => { if (mounted) setActors(r.data?.actors || []) })
      .catch(() => { if (mounted) setActors([]) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [cveId])

  if (loading || !actors || actors.length === 0) return null

  const catColor = (c: string) =>
    c === 'nation-state' ? 'text-rose-light bg-rose/10 border-rose/30'
    : c === 'ransomware' ? 'text-purple-300 bg-purple-500/10 border-purple-500/30'
    : c === 'criminal'   ? 'text-amber-light bg-amber/10 border-amber/30'
    : 'text-text-muted bg-surface-raised border-border'

  return (
    <div className="card p-3 mt-2">
      <div className="flex items-center gap-2 mb-2">
        <UsersIcon size={12} className="text-blue-light" />
        <h3 className="text-[11px] font-semibold text-text-primary">Linked threat actors</h3>
        <span className="text-[9px] text-text-muted">({actors.length})</span>
      </div>
      <div className="space-y-1">
        {actors.slice(0, 6).map(a => (
          <div key={a.id} className="flex items-center gap-2 text-[10px]">
            <span className={clsx('px-1 py-0.5 rounded border text-[8px] font-mono uppercase', catColor(a.category))}>
              {a.category?.split('-')[0] || '?'}
            </span>
            <span className="font-mono text-text-primary font-semibold">{a.name}</span>
            <span className="text-text-muted truncate">{a.sponsor}</span>
            {a.structured && <span className="text-[8px] text-emerald-light" title="MITRE-curated attribution">✓ MITRE</span>}
            {!a.structured && a.evidence_url && (
              <a href={a.evidence_url} target="_blank" rel="noreferrer noopener"
                 className="text-blue-light hover:underline ml-auto" title={`Source: ${a.evidence_src}`}>
                <ExternalLink size={9} />
              </a>
            )}
          </div>
        ))}
      </div>
      <p className="text-[8px] text-text-muted mt-2 italic">
        Best-effort attribution from MITRE STIX + news co-mentions.
      </p>
    </div>
  )
}
